/**
 * Subscription context for CiteOps.ai Dashboard
 *
 * Fetches the authenticated user's subscription row from Supabase and
 * keeps it up-to-date via a Realtime channel.
 *
 * Exposes:
 * - subscription (Subscription | null)
 * - tier (effective SubscriptionTier, defaults to 'free')
 * - loading
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from './AuthContext'
import type { Subscription, SubscriptionTier } from '../types/subscription'
import { getEffectiveTier } from '../types/subscription'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface SubscriptionContextValue {
  /** The raw subscription row, or null if not yet loaded / no subscription. */
  subscription: Subscription | null
  /** The effective tier ('free' | 'pro' | 'team'), accounting for status. */
  tier: SubscriptionTier
  /** True while the subscription is being fetched. */
  loading: boolean
  /** Re-fetch subscription from the database (e.g. after checkout). */
  refetch: () => Promise<void>
}

/* ------------------------------------------------------------------ */
/*  Context                                                            */
/* ------------------------------------------------------------------ */

const SubscriptionContext = createContext<SubscriptionContextValue | undefined>(
  undefined,
)

/* ------------------------------------------------------------------ */
/*  Provider                                                           */
/* ------------------------------------------------------------------ */

export interface SubscriptionProviderProps {
  children: ReactNode
}

export function SubscriptionProvider({ children }: SubscriptionProviderProps) {
  const { user } = useAuth()
  const [subscription, setSubscription] = useState<Subscription | null>(null)
  const [loading, setLoading] = useState(true)

  /* ---------- fetch subscription when user changes ---------- */

  // Depend on user.id (not the user object) to avoid redundant fetches
  // when AuthContext emits multiple user object references during init
  // (getSession + onAuthStateChange both call setUser with a new object).
  const userId = user?.id ?? null

  const fetchSubscription = useCallback(async () => {
    if (!userId) {
      setSubscription(null)
      setLoading(false)
      return
    }

    setLoading(true)
    const { data, error } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', userId)
      .single()

    if (error) {
      // PGRST116 = "no rows returned" which is expected for new/free users
      if (error.code !== 'PGRST116') {
        console.error('[SubscriptionContext] fetch error:', error.message)
      }
      setSubscription(null)
    } else {
      setSubscription(data as Subscription)
    }

    setLoading(false)
  }, [userId])

  useEffect(() => {
    if (!userId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSubscription(null)
      setLoading(false)
      return
    }

    let cancelled = false
    ;(async () => {
      await fetchSubscription()
      if (cancelled) return
    })()

    return () => {
      cancelled = true
    }
  }, [userId, fetchSubscription])

  /* ---------- realtime subscription updates ---------- */

  useEffect(() => {
    if (!userId) return

    const channel = supabase
      .channel(`subscriptions:user_id=eq.${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'subscriptions',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          if (payload.eventType === 'DELETE') {
            setSubscription(null)
          } else {
            setSubscription(payload.new as Subscription)
          }
        },
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [userId])

  /* ---------- derived tier ---------- */

  const tier = getEffectiveTier(subscription, user?.email)

  /* ---------- memoised value ---------- */

  const value = useMemo<SubscriptionContextValue>(
    () => ({ subscription, tier, loading, refetch: fetchSubscription }),
    [subscription, tier, loading, fetchSubscription],
  )

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  )
}

/* ------------------------------------------------------------------ */
/*  Hook                                                               */
/* ------------------------------------------------------------------ */

/**
 * Consume the SubscriptionContext.
 * Must be rendered inside a <SubscriptionProvider> (which itself must
 * be inside an <AuthProvider>).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useSubscription(): SubscriptionContextValue {
  const ctx = useContext(SubscriptionContext)
  if (ctx === undefined) {
    throw new Error(
      'useSubscription must be used within a SubscriptionProvider',
    )
  }
  return ctx
}
